import React, { useMemo, useCallback, useRef } from 'react';
import type { CheckboxProps } from '@app/ui';
import { Masonry } from 'masonic';
import * as R from 'ramda';

import usePrevious from '@app/components/hooks/usePrevious';
import isVideoType from '@app/utils/isVideo';
import SocialPost from '@app/components/posts/SocialPost';

import { Post } from '@app/social-media-posts/types';
import { getCarouselPostIds } from '@app/social-media-posts/carouselGroups';
import {
    createSelectionStore,
    SelectionStoreContext,
    SelectionSync,
    type SelectedMap,
} from './selectionStore';
import './SocialPosts.scss';
import '@app/css/media/styles.scss';
import '@app/styles/custom-myles-styles.scss';

export type SocialPostsProps = {
    posts: any;
    postsOrder: string[];
    currency: string;
    isSelectMode?: boolean;
    selected?: SelectedMap;
    onSelectPost?: (postId: number) => void;
    onDetailedView?: (post: Post) => void;
    collections: { [key: string]: boolean };
};

const getMetricNumber = (value?: number | string | null): number => {
    const numericValue =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const IMAGE_MEDIA_TYPE_ID = 1;
const VIDEO_MEDIA_TYPE_ID = 2;
const TEXT_MEDIA_TYPE_ID = 3;
const DEFAULT_CARD_HEIGHT = 200;

export const getCardSponsorMediaValue = (post: Post): number | undefined => {
    const carouselSlides = post.carousel ? post.carouselSlides ?? [] : [];
    if (!carouselSlides.length) {
        return post.totalSponsorMediaValue;
    }
    return carouselSlides.reduce(
        (sum, slide) => sum + getMetricNumber(slide.totalSponsorMediaValue),
        0,
    );
};

export const getCardDisplayPost = (post: Post): Post => {
    const carouselSlides = post.carousel ? post.carouselSlides ?? [] : [];
    return carouselSlides[0] ?? post;
};

export const getExpectedCardHeight = (
    displayPost: Post,
    expectedWidth: number,
): number => {
    const mediaTypeId = displayPost?.mediaType?.id ?? IMAGE_MEDIA_TYPE_ID;
    if (mediaTypeId === TEXT_MEDIA_TYPE_ID) {
        return Math.min(500, Math.max(100, displayPost.content?.length ?? 0));
    }

    const height = getMetricNumber(displayPost?.height);
    const width = getMetricNumber(displayPost?.width);
    if (!height || !width) {
        return DEFAULT_CARD_HEIGHT;
    }

    return Math.min(500, (expectedWidth * height) / width);
};

export const getCollectionNames = (
    post: Post,
    collections: SocialPostsProps['collections'],
): Array<string | undefined> | undefined => {
    if (!post?.collectionIds) return undefined;
    return post.collectionIds.map(id => R.path([id, 'name'], collections));
};

const listsEqual = <T,>(prev?: T[], next?: T[]) => {
    if (prev === next) return true;
    if (!prev || !next || prev.length !== next.length) return false;
    for (let index = 0; index < prev.length; index += 1) {
        if (prev[index] !== next[index]) return false;
    }
    return true;
};

export type GridPostCard = {
    id: string | number;
    post: Post;
    displayPost: Post;
    isVideo: boolean;
    isCarousel: boolean;
    expectedHeight: number;
    isTextPost: boolean;
    selectionIds: Array<string | number>;
    collectionNames?: Array<string | undefined>;
    isSelectMode: boolean;
    onSelect: CheckboxProps['onChange'];
    currency: string;
    width: number;
};

type BuildPostCardsArgs = {
    previous: GridPostCard[];
    postsOrder: string[];
    posts: Record<string, Post>;
    collections: SocialPostsProps['collections'];
    isSelectMode: boolean;
    currency: string;
    expectedWidth: number;
    getOnSelect: (postId: number) => CheckboxProps['onChange'];
};

export const getStablePostCards = ({
    previous,
    postsOrder,
    posts,
    collections,
    isSelectMode,
    currency,
    expectedWidth,
    getOnSelect,
}: BuildPostCardsArgs): GridPostCard[] => {
    const previousById = new Map(previous.map(card => [String(card.id), card]));
    const next: GridPostCard[] = [];

    postsOrder.forEach(postId => {
        const post = posts[postId];
        if (!post) return;

        const collectionNames = getCollectionNames(post, collections);
        const previousCard = previousById.get(String(post.id));
        if (
            previousCard &&
            previousCard.post === post &&
            previousCard.isSelectMode === isSelectMode &&
            previousCard.currency === currency &&
            listsEqual(previousCard.collectionNames, collectionNames)
        ) {
            next.push(previousCard);
            return;
        }

        const displayPost = getCardDisplayPost(post);
        const mediaTypeId = displayPost?.mediaType?.id ?? IMAGE_MEDIA_TYPE_ID;
        const isVideo =
            mediaTypeId === VIDEO_MEDIA_TYPE_ID &&
            isVideoType(displayPost.cdnMediaUrl);
        const selectionIds = getCarouselPostIds(post);
        const isCarousel = !!post.carousel && selectionIds.length > 1;
        const isTextPost = mediaTypeId === TEXT_MEDIA_TYPE_ID;

        next.push({
            id: post.id,
            post,
            displayPost,
            isVideo,
            isCarousel,
            expectedHeight: getExpectedCardHeight(displayPost, expectedWidth),
            isTextPost,
            selectionIds,
            collectionNames,
            isSelectMode,
            onSelect: getOnSelect(Number(post.id)),
            currency,
            width: expectedWidth,
        });
    });

    if (next.length === previous.length) {
        const unchanged = next.every((card, index) => card === previous[index]);
        if (unchanged) return previous;
    }

    return next;
};

const SocialPosts = ({
    posts,
    postsOrder,
    currency,
    isSelectMode = false,
    selected = {},
    onSelectPost,
    onDetailedView,
    collections,
}: SocialPostsProps) => {
    const expectedWidth = 201;
    const hasPostsAvailable = postsOrder.length > 0;
    const onSelectPostRef = useRef(onSelectPost);
    const onDetailedViewRef = useRef(onDetailedView);
    onSelectPostRef.current = onSelectPost;
    onDetailedViewRef.current = onDetailedView;

    const onSelectHandlers = useRef(
        new Map<number, CheckboxProps['onChange']>(),
    );
    const getOnSelect = useCallback((postId: number) => {
        const existing = onSelectHandlers.current.get(postId);
        if (existing) return existing;
        const handler: CheckboxProps['onChange'] = () => {
            onSelectPostRef.current?.(postId);
        };
        onSelectHandlers.current.set(postId, handler);
        return handler;
    }, []);

    const selectionStoreRef = useRef<ReturnType<
        typeof createSelectionStore
    > | null>(null);
    if (selectionStoreRef.current === null) {
        selectionStoreRef.current = createSelectionStore(selected);
    }
    const selectionStore = selectionStoreRef.current;

    const cardsRef = useRef<GridPostCard[]>([]);
    const postCards = useMemo(() => {
        const next = getStablePostCards({
            previous: cardsRef.current,
            postsOrder,
            posts,
            collections,
            isSelectMode,
            currency,
            expectedWidth,
            getOnSelect,
        });
        cardsRef.current = next;
        return next;
    }, [postsOrder, posts, collections, isSelectMode, currency, getOnSelect]);

    const renderPost = useCallback((props: { data: GridPostCard }) => {
        const {
            post,
            displayPost,
            isVideo,
            isCarousel,
            expectedHeight,
            isTextPost,
            selectionIds,
            collectionNames,
            isSelectMode: cardSelectMode,
            onSelect,
            currency: cardCurrency,
            width,
        } = props.data;

        return (
            <SocialPost
                key={post.id}
                src={displayPost.cdnMediaUrl}
                postUrl={post.postUrl}
                sourceType={post.socialPlatform}
                createdAt={post.createdAt}
                isVideo={isVideo}
                isCarousel={isCarousel}
                isSelectMode={cardSelectMode}
                selectionIds={selectionIds}
                sponsorMediaValue={getCardSponsorMediaValue(post)}
                currency={cardCurrency}
                width={width}
                height={expectedHeight}
                socialAccount={post.socialAccount}
                onMediaClick={() => onDetailedViewRef.current?.(post)}
                onSelect={onSelect}
                content={post.content}
                isTextPost={isTextPost}
                collectionNames={collectionNames}
            />
        );
    }, []);

    const itemsCount = postCards?.length;
    const prevItemsCount = usePrevious(itemsCount);
    const removesCount = useRef(0);

    const gridKeyPostBag = useMemo(() => {
        if (!itemsCount || !prevItemsCount) {
            return removesCount.current;
        }
        if (itemsCount < prevItemsCount) {
            removesCount.current += 1;
            return removesCount.current;
        }

        return removesCount.current;
    }, [itemsCount, prevItemsCount]);

    const masonry = useMemo(
        () =>
            hasPostsAvailable ? (
                <Masonry
                    key={`key-${gridKeyPostBag}`}
                    items={postCards}
                    columnGutter={20}
                    columnWidth={200}
                    overscanBy={4}
                    render={renderPost}
                />
            ) : null,
        [hasPostsAvailable, gridKeyPostBag, postCards, renderPost],
    );

    return (
        <SelectionStoreContext.Provider value={selectionStore}>
            <SelectionSync store={selectionStore} selected={selected} />
            <div
                className="v2-grid-container"
                data-test="social-posts-container"
            >
                {masonry}
            </div>
        </SelectionStoreContext.Provider>
    );
};

export default SocialPosts;
